/**
 * kerf JSX runtime.
 *
 * JSX renders to `SafeHtml`, which wraps both:
 *   - `__html`: the flattened HTML string (what `toString()` returns; what
 *     legacy/SSR consumers care about)
 *   - `__segment`: a structured representation that distinguishes "static
 *     html", "keyed list", and "mixed" content.
 *
 * Most renders are pure-static and the segment is just `{kind:'static',html}`.
 * When the tree contains a list (via `each()`) or a parent whose children
 * include a non-static segment, the runtime threads that structure up so
 * `mount()` can dispatch on it — running its native keyed reconciler for
 * the list parts and leaving the static surrounds to the general-purpose
 * diff.
 *
 * Configure in your `tsconfig.json`:
 *
 *     "jsx": "react-jsx",
 *     "jsxImportSource": "kerfjs"
 *
 * Then write JSX as you normally would — kerf provides the `jsx` /
 * `jsxs` / `jsxDEV` / `Fragment` exports the JSX transform looks for.
 */

import { bindAttr, bindMarkerAttr, bindText, isSignal } from './bindings.js';
import type { KerfBuiltinIntrinsicElements } from './jsx-types.js';
import type { ReadonlySignal, Signal } from './reactive.js';
import {
  flatten,
  type ListSegment,
  mergeChildSegments,
  type Segment,
  wrapWithTags,
} from './segment.js';
import { escapeAttr, escapeHtml } from './utils/escapeHtml.js';
import { ATTR_ALIASES } from './utils/jsx-attr-aliases.js';
import { isDangerousUrlValue, reportDangerousUrl } from './utils/urlScreen.js';

// Cross-realm/cross-bundle brand. Using `Symbol.for` (the global registry)
// means two `SafeHtml` classes from different module copies still recognize
// each other. Same approach React uses for `$$typeof: Symbol.for('react.element')`.
// Without this, `instanceof SafeHtml` fails when the consumer's bundler ends
// up loading two copies of kerf (separate barrel + jsx-runtime entries,
// monorepo dedup misses, ESM/CJS interop, etc.).
const SAFE_HTML_BRAND = Symbol.for('kerfjs.SafeHtml');

export class SafeHtml {
  readonly __html: string;
  readonly __segment: Segment;
  // Branded so `isSafeHtml()` recognizes instances from any copy of this module.
  readonly [SAFE_HTML_BRAND] = true as const;
  constructor(input: string | Segment) {
    if (typeof input === 'string') {
      this.__segment = { kind: 'static', html: input };
      this.__html = input;
    } else {
      this.__segment = input;
      this.__html = flatten(input, false);
    }
  }
  toString(): string {
    return this.__html;
  }
}

/**
 * Type guard for `SafeHtml`. Prefer this over `instanceof SafeHtml` — it works
 * across module copies (e.g. when the consumer's bundler loads kerf's barrel
 * and JSX-runtime entries as independent modules).
 *
 * Security note (KF-321): this is a duck-check on the global `Symbol.for` brand,
 * so same-realm code *can* forge a "trusted" value — `{ [SAFE_HTML_BRAND]: true,
 * __html: '<img onerror=…>' }` passes and bypasses escaping + the URL screen.
 * This is intentional and not a vulnerability: minting the brand requires a
 * Symbol key, which no data channel (JSON.parse, form/query/localStorage,
 * structuredClone, JSON-based prototype-pollution) can produce — those all yield
 * string keys. The only way to forge it is to run JS that writes the symbol, and
 * such code can equally `import { raw }`. Forgery therefore grants no capability
 * an attacker with code execution lacks — the same posture as React's
 * `$$typeof: Symbol.for('react.element')`. The global `Symbol.for` (vs a
 * module-private symbol) is a deliberate cross-bundle-recognition tradeoff (see
 * the `SAFE_HTML_BRAND` note above); a private symbol would additionally block
 * same-realm forgery, but only closes a non-threat at the cost of that
 * recognition, so it's kept global by design.
 */
export function isSafeHtml(value: unknown): value is SafeHtml {
  return typeof value === 'object'
    && value !== null
    && (value as Record<symbol, unknown>)[SAFE_HTML_BRAND] === true;
}

/**
 * Inject a pre-escaped HTML string verbatim, bypassing kerf's auto-escaping.
 *
 * **Reach for this rarely.** kerf escapes automatically everywhere else, so a lot
 * of `raw()` in a codebase is usually a sign the wrong tool is being used — the
 * common cases have a safer, first-class answer:
 *  - Interpolating dynamic text/attributes? Plain JSX (`<p>{value}</p>`,
 *    `class={sig}`) already escapes it; you don't need `raw()`.
 *  - Composing markup? Build a {@link SafeHtml} the normal way — a JSX expression,
 *    the `html` tagged template (`kerfjs/html`), `each()`, or a component function
 *    that returns JSX. Those all produce trusted `SafeHtml` without hand-writing
 *    an HTML string.
 *  - A genuinely trusted, pre-escaped **dynamic** value (server output, config, a
 *    hard-coded string)? That's the one legitimate use. The
 *    `kerfjs/no-raw-with-dynamic-arg` lint rule flags a `raw()` whose argument is
 *    not a literal — because unsanitized user input is the canonical XSS mistake —
 *    so acknowledge it with a `// eslint-disable-next-line
 *    kerfjs/no-raw-with-dynamic-arg` at that call. That explicit override is the
 *    single sanctioned way to say "I've reviewed this; it's trusted," and it
 *    leaves a searchable audit trail.
 *
 * `raw()` is NOT a sanitizer — it does no escaping. For user-controlled input,
 * sanitize first (`raw(DOMPurify.sanitize(marked(userMarkdown)))`) or, better,
 * render it through escaping JSX instead.
 */
export function raw(html: string): SafeHtml {
  return new SafeHtml(html);
}

/**
 * Internal: build a `SafeHtml` representing a list segment. Used by
 * `each()` so the JSX runtime is the sole owner of `SafeHtml` construction.
 */
export function listSafeHtml(
  id: string,
  items: ListSegment['items'],
  source?: object,
): SafeHtml {
  return new SafeHtml({ kind: 'list', id, items, source });
}

/**
 * Internal: build a `SafeHtml` representing a granular list segment with
 * patches (KF-92). The reconciler applies the patches to the existing
 * binding directly, skipping the per-item iteration that the snapshot
 * `listSafeHtml` requires. `items` is included for fall-through paths
 * (toString during SSR, fall-back when the binding doesn't exist yet).
 *
 * Patch HTML is rendered upstream (in `each()`) inside a try/catch — see
 * KF-99 — so by the time we get here every `update` / `insert` patch
 * already carries a `html` string, and the reconciler does no further
 * row rendering.
 */
export function granularListSafeHtml(
  id: string,
  items: ListSegment['items'],
  patches: NonNullable<ListSegment['patches']>,
  source?: object,
): SafeHtml {
  return new SafeHtml({ kind: 'list', id, items, patches, source });
}

// KF-294: `ReadonlySignal<unknown>` (covariant) accepts both `signal()` and
// `computed()` values of any T handed straight into a text hole — the runtime
// binds them fine-grained instead of stringifying.
type Child = SafeHtml | string | number | boolean | null | undefined | ReadonlySignal<unknown>;
type Children = Child | Children[];

interface Props {
  children?: Children;
  [key: string]: unknown;
}

const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'source', 'track', 'wbr',
]);

/**
 * Convert a single JSX child into a Segment. Handles SafeHtml passthrough,
 * primitive coercion + escaping, arrays (recursive), and the nullish/false
 * skip cases.
 */
function toSegment(child: Children): Segment {
  if (child == null || typeof child === 'boolean') return { kind: 'static', html: '' };
  // KF-294: a signal handed straight into a text position. Inside a mount
  // render, emit a comment marker and record a binding so the text node
  // updates fine-grained (no render re-run). Outside a mount (SSR/toString),
  // snapshot the current value as escaped text.
  if (isSignal(child)) {
    const marker = bindText(child);
    if (marker !== null) return { kind: 'static', html: marker };
    const v = (child as Signal<unknown>).value;
    return { kind: 'static', html: v == null || typeof v === 'boolean' ? '' : escapeHtml(String(v)) };
  }
  if (isSafeHtml(child)) {
    // Cross-bundle SafeHtml shims (KF-14 case) may have only `__html`.
    return child.__segment ?? { kind: 'static', html: child.__html };
  }
  if (typeof child === 'string') return { kind: 'static', html: escapeHtml(child) };
  if (typeof child === 'number') return { kind: 'static', html: String(child) };
  if (Array.isArray(child)) return mergeChildSegments(child.map(toSegment));
  // Catch the common mistake of passing a DOM element (e.g. the result of
  // toElement(...)) as a JSX child. The runtime renders to HTML strings, so
  // DOM nodes can't be composed — they'd silently serialize to "" and their
  // event listeners would be lost. Throw loudly so this can't sneak in.
  const maybeNode = child as unknown;
  if (typeof maybeNode === 'object' && maybeNode !== null
      && ('nodeType' in maybeNode || 'outerHTML' in maybeNode)) {
    throw new Error(
      'JSX: DOM elements cannot be passed as children (the JSX runtime renders to HTML strings). '
      + 'Build the tree in one JSX expression and use querySelector after toElement() to get element refs.',
    );
  }
  throw new Error(
    `JSX: unsupported child of type ${describeValue(child)}. `
    + 'Children must be SafeHtml, string, number, boolean, null, undefined, or an array of those. '
    + 'Common mistakes: passing a Signal/Store object directly (use signal.value or store.state.value), '
    + 'passing a function (call it first), or passing a Promise (await it before render).',
  );
}

function describeValue(v: unknown): string {
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'object' && v !== null) {
    const ctor = (v as { constructor?: { name?: string } }).constructor?.name;
    return ctor && ctor !== 'Object' ? `object (${ctor})` : 'object';
  }
  return typeof v;
}

/**
 * A well-formed HTML/SVG attribute name: a letter/underscore/colon, then
 * letters, digits, or `_.:-` — covers `class`, `data-id`, `aria-label`,
 * `xlink:href`, `xmlns:xlink`, `stroke-width`, `viewBox`, etc. A name outside
 * this shape can only come from spreading untrusted KEYS into JSX
 * (`<div {...obj}>`), and is exactly the shape that could carry the whitespace,
 * `>`, `=`, quotes, or control chars needed to break out of the open tag and
 * inject markup — so `renderAttr` rejects it (KF-306).
 */
const SAFE_ATTR_NAME = /^[A-Za-z_:][\w.:-]*$/;

/**
 * Reject an attribute NAME that kerf must never route into HTML or the DOM.
 * Shared by BOTH attribute paths so they enforce one contract (KF-306, KF-322):
 *
 *   - the static string path (`renderAttr`) — a name is emitted verbatim into
 *     the open tag, and `on*` values become live inline handlers once parsed;
 *   - the fine-grained bound path (`jsx()` signal branch → `bindAttr` →
 *     `setBoundAttr` → `el.setAttribute(name, …)`) — `setAttribute('onclick', …)`
 *     installs a LIVE handler in the browser, an XSS vector that would otherwise
 *     bypass the static guard entirely.
 *
 * Two rejections:
 *   1. `on*` (any case) — kerf's model is event delegation, never inline
 *      handlers. `isFn` tailors the message: function values get the
 *      delegate() migration pointer; string/signal/other values get the
 *      XSS-aware message.
 *   2. a malformed name (spread of untrusted keys, `<div {...obj}>`) that could
 *      break out of the open tag. The bound path can't actually inject markup
 *      this way — `setAttribute` throws `InvalidCharacterError` rather than
 *      parsing — but the same guard runs on both paths for one consistent
 *      contract. Validated post-alias; every `ATTR_ALIASES` value is itself a
 *      valid name, so aliasing is unaffected.
 */
function assertEmittableAttrName(key: string, name: string, isFn: boolean): void {
  if (/^on[a-z]/i.test(name)) {
    if (isFn) {
      throw new Error(
        `JSX: inline event handlers like ${key}={fn} are not supported by kerf's JSX → HTML-string runtime. `
        + 'Use event delegation from the mount root instead:\n\n'
        + '  delegate(rootEl, \'click\', \'[data-action="..."]\', (evt, target) => { ... });\n'
        + '  <button data-action="...">click</button>\n\n'
        + 'See docs/5-event-delegation.md for the tier-1/tier-2/tier-3 model.',
      );
    }
    throw new Error(
      `JSX: event-handler attribute ${JSON.stringify(key)} is not allowed — an `
      + '`on*` attribute (whether a string emitted into HTML or a signal bound via '
      + 'setAttribute) installs a live inline handler, an XSS vector. '
      + 'kerf uses event delegation: delegate(rootEl, \'click\', \'[data-action="..."]\', handler). '
      + 'See docs/5-event-delegation.md.',
    );
  }
  if (!SAFE_ATTR_NAME.test(name)) {
    throw new Error(
      `JSX: invalid attribute name ${JSON.stringify(key)}. Attribute names must be a `
      + 'letter/underscore/colon followed by letters, digits, or "_.:-" (e.g. class, '
      + 'data-id, aria-label, xlink:href). This usually means an untrusted object was '
      + 'spread into JSX ({...obj}) with attacker-controlled keys — validate keys first.',
    );
  }
}

function renderAttr(key: string, value: unknown): string {
  return renderAttrNamed(key, ATTR_ALIASES[key] ?? key, value);
}

/**
 * Core attribute renderer. `key` is the author-written attribute key (used in
 * error messages); `name` is the final emitted attribute name — post-alias for
 * JSX, verbatim for the `kerfjs/html` tagged-template path.
 */
function renderAttrNamed(key: string, name: string, value: unknown): string {
  if (value == null || value === false) return '';
  assertEmittableAttrName(key, name, typeof value === 'function');
  if (value === true) return ` ${name}`;
  let strValue: string;
  if (isSafeHtml(value)) {
    strValue = value.__html;
  } else if (typeof value === 'number') {
    strValue = String(value);
  } else if (typeof value === 'string') {
    // URL-screening shared with the fine-grained binding writer (KF-297).
    // `SafeHtml` values (raw()) skip this — they hit the branch above.
    // KF-340: throw in dev (fail loudly), warn+drop in prod.
    if (isDangerousUrlValue(name, value)) {
      reportDangerousUrl('JSX', name, value);
      return '';
    }
    strValue = escapeAttr(value);
  } else {
    throw new Error(
      `JSX: unsupported value for attribute "${key}" — got ${describeValue(value)}. `
      + 'Attribute values must be string, number, boolean, null, undefined, or SafeHtml. '
      + 'Did you mean to read .value off a Signal, or stringify the object first?',
    );
  }
  return ` ${name}="${strValue}"`;
}

export function jsx(tag: string | ((props: Props) => SafeHtml), props: Props): SafeHtml {
  if (typeof tag === 'function') return tag(props);

  const { children, ...attrs } = props;
  let attrStr = '';
  let bindIds: string[] | null = null;
  for (const [k, v] of Object.entries(attrs)) {
    // KF-294: a signal handed straight into an attribute. Inside a mount
    // render, register a binding and mark the element (via `data-kfb` for
    // static-surround holes / `data-kfbrow` for each()-row holes) instead of
    // emitting the attribute — the wiring pass sets it after parse and keeps
    // it fine-grained. Outside a mount, snapshot the current value.
    if (isSignal(v)) {
      const name = ATTR_ALIASES[k] ?? k;
      // Reject `on*` / malformed names on the bound path too (KF-322 completes
      // KF-306). Unconditional: a signal's value can change over time, so we
      // can't gate on the current value — an `onclick={signal}` binding would
      // otherwise install a live inline handler via setAttribute, bypassing the
      // static renderAttr guard entirely. `isFn` is false: a signal is not a
      // function value, so this routes to the XSS-aware message.
      assertEmittableAttrName(k, name, false);
      const id = bindAttr(name, v);
      if (id !== null) {
        (bindIds ??= []).push(id);
        continue;
      }
      attrStr += renderAttr(k, (v as Signal<unknown>).value);
      continue;
    }
    attrStr += renderAttr(k, v);
  }
  // All signal attrs on one element share a scope, so the marker attr name is
  // fetched once (avoids a per-attr object alloc from bindAttr).
  if (bindIds !== null) attrStr += ` ${bindMarkerAttr()}="${bindIds.join(',')}"`;

  if (VOID_TAGS.has(tag)) return new SafeHtml(`<${tag}${attrStr}>`);

  const childSegment: Segment = children != null
    ? toSegment(children)
    : { kind: 'static', html: '' };
  return new SafeHtml(wrapWithTags(childSegment, `<${tag}${attrStr}>`, `</${tag}>`));
}

export { jsx as jsxs };
// vitest's dev-mode JSX transform emits `jsxDEV(tag, props, ...)`; the
// alias lets tests import this module without the production build pipeline
// caring.
export { jsx as jsxDEV };

export function Fragment({ children }: { children?: Children }): SafeHtml {
  return new SafeHtml(children != null ? toSegment(children) : { kind: 'static', html: '' });
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace JSX {
  export type Element = SafeHtml;
  export interface ElementChildrenAttribute {
    children: unknown;
  }
  // Per-tag attribute contracts live in `./jsx-types.ts` as
  // `KerfBuiltinIntrinsicElements`. Re-exposed as an **interface** (not a
  // type alias) so consumers can declaration-merge custom-element tags
  // (KF-100):
  //
  //     declare module 'kerfjs/jsx-runtime' {
  //       namespace JSX {
  //         interface IntrinsicElements {
  //           'my-element': KerfCustomElement & { foo?: string };
  //         }
  //       }
  //     }
  //
  // KF-123: the imported interface is named `KerfBuiltinIntrinsicElements`
  // upstream so tsup/tsc cannot strip an import alias and end up emitting
  // `interface IntrinsicElements extends IntrinsicElements {}` in the .d.ts
  // — that shadowed form self-resolves to empty and breaks every `<tag>` in
  // consumer .tsx with TS2339. Verified against `dist/jsx-runtime.d.ts` by
  // `tests/dist/jsx-typing/` on every `npm run build`.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface IntrinsicElements extends KerfBuiltinIntrinsicElements {}
}

/**
 * Internal coordination exports for the `kerfjs/html` tagged-template
 * front-end (`src/html.ts`). Underscore-prefixed and deliberately NOT in the
 * main barrel — the contract is that `html\`\`` routes through the exact same
 * value semantics as JSX (text holes: `_toSegment`; attribute holes:
 * `_assertEmittableAttrName` + `_renderAttrVerbatim`), so the two authoring
 * paths cannot drift. `_renderAttrVerbatim` skips the camelCase
 * `ATTR_ALIASES` table: template authors write real HTML attribute names
 * (`class`, not `className`).
 */
export function _toSegment(child: unknown): Segment {
  return toSegment(child as Children);
}

export function _renderAttrVerbatim(name: string, value: unknown): string {
  return renderAttrNamed(name, name, value);
}

export { assertEmittableAttrName as _assertEmittableAttrName };

/**
 * Public re-exports of the JSX type primitives so consumers can compose
 * attribute interfaces for custom elements without reaching into
 * `kerfjs/jsx-types` (which is intentionally not in `package.json#exports`).
 */
export type {
  AttrLike,
  AttrValue,
  DataAriaAttrs,
  KerfBaseAttrs,
  KerfCustomElement,
} from './jsx-types.js';
